import { assign, createMachine, fromPromise } from 'xstate';

import { getUserProfile } from '~/_v3/account/profile/actors/profile.http';
import type { ProfileInfo } from '~/_v3/account/profile/domain/profile';
import { logoutFranceConnect } from '~/_v3/france-connect/actors/fc.http';
import { refreshToken } from '~/_v3/login/actors/login.http';

import { getHealth } from '../actors/app.http';
import { authTokenExists } from './app.guards';
import { setProfile, setRedirectLogoutFranceConnectUri, setVersion } from './app.machine.actions';

export const config = {
	actors: {
		getUserProfile: fromPromise(getUserProfile),
		logoutFranceConnect: fromPromise(logoutFranceConnect),
		getHealth: fromPromise(getHealth)
	},
	actions: {
		setProfile: assign(setProfile),
		refreshToken: assign(refreshToken),
		setRedirectLogoutFranceConnectUri: assign(setRedirectLogoutFranceConnectUri),
		setVersion: assign(setVersion)
	},
	guards: {
		authTokenExists
	}
};

export type AppContext = {
	userProfile: ProfileInfo;
	franceConnectLogoutRedirectUri: string;
	version: string;
};

export type AuthActorEvents =
	| { type: 'xstate.done.actor.getUserProfile'; actorId: 'getUserProfile'; output: ProfileInfo }
	| { type: 'xstate.done.actor.getHealth'; actorId: 'getHealth'; output: { version: string; revision: string } }
	| { type: 'xstate.done.actor.logoutFranceConnect'; actorId: 'logoutFranceConnect'; output: { logoutUrl: string } };

export const machine = {
	context: { userProfile: {}, franceConnectLogoutRedirectUri: '', version: '' } as AppContext,
	id: 'app_machine',
	initial: 'loading_app',
	states: {
		loading_app: {
			on: {
				LOAD_PROFILE: 'loading_user_profile_from_server'
			},
			always: {
				target: 'loading_user_profile_from_server',
				guard: 'authTokenExists'
			}
		},
		loading_user_profile_from_server: {
			invoke: {
				id: 'getUserProfile',
				src: 'getUserProfile',
				onDone: {
					actions: 'setProfile',
					target: 'getting_health_from_server'
				},
				onError: {
					target: 'getting_health_from_server'
				}
			}
		},
		displaying_app: {
			on: {
				LOAD_PROFILE: 'loading_user_profile_from_server',
				REFRESH_TOKEN: { target: 'loading_user_profile_from_server', actions: 'refreshToken' },
				LOGOUT_FRANCE_CONNECT: 'logging_out_france_connect_from_server'
			}
		},

		getting_health_from_server: {
			invoke: {
				id: 'getHealth',
				src: 'getHealth',
				onDone: {
					actions: 'setVersion',
					target: 'displaying_app'
				},
				onError: {
					target: 'displaying_app'
				}
			}
		},
		logging_out_france_connect_from_server: {
			invoke: {
				id: 'logoutFranceConnect',
				src: 'logoutFranceConnect',
				onDone: {
					target: 'displaying_app',
					actions: 'setRedirectLogoutFranceConnectUri'
				},
				onError: {
					target: 'displaying_app'
				}
			}
		},
		final: {}
	}
};

export const appMachine = createMachine(machine).provide(config);
